import React, { useCallback, useContext, useMemo } from 'react';
import { SelectField } from '@ubeya/shared-web/components';
import useAddNewSelector from '@ubeya/shared-web/hooks/useAddNewSelector';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import usePositionsGroups from '@ubeya/shared/hooks/account/usePositionsGroups';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { hasRestrictedFeatures, CHCO_ORGANIZATION_ID } from '@ubeya/shared/utils/accounts';
import { filterPositionOptionsByBranchId } from '@ubeya/shared-web/utils/positions';
import { extractPositionIdsOptions, generatePositionOptions } from 'shared/utils/positions';
import { PositionSelectFieldOption } from './PositionSelectFieldOption';
import PositionModal from 'settings/components/AccountSettings/Positions/PositionModal';

const PositionsSelectField = React.forwardRef(
  (
    {
      name = 'positionId',
      title,
      onAdd,
      isMulti = false,
      enableAddNew = true,
      hideTitle = false,
      customPositionsOptions,
      inputOnChange,
      includePositionGroups = false,
      modalClassName,
      branchId,
      ...restSelectProps
    },
    ref
  ) => {
    const { t, organizationId } = useContext(ConfigContext);
    const { accountId } = useAccount();

    const { positionsOptions: allPositionsOptions, mappedPositions } = usePositions();
    const { positionsGroups } = usePositionsGroups();

    const defaultPositionsOptions = useMemo(
      () => filterPositionOptionsByBranchId({ positionsOptions: allPositionsOptions, mappedPositions, branchId }),
      [allPositionsOptions, mappedPositions, branchId]
    );

    const myPrivileges = useContext(PrivilegesContext);
    const enableAddNewPosition =
      enableAddNew &&
      !!myPrivileges[privileges.ACCOUNT_ADMIN] &&
      !hasRestrictedFeatures({ accountId, organizationId }) &&
      organizationId !== CHCO_ORGANIZATION_ID;

    const {
      isOpen: positionModal,
      setIsOpen: setPositionModal,
      options: positionsOptions,
      onChange: onChangePosition
    } = useAddNewSelector({
      options: customPositionsOptions ?? defaultPositionsOptions,
      addText: 'addNewPosition',
      isMulti,
      enable: enableAddNewPosition
    });

    const options = generatePositionOptions({
      includePositionGroups,
      positionsOptions,
      positionsGroups,
      t
    });

    const onChange = useCallback(
      (newId, input, isAdded, addedData) => {
        let positionIds = newId;
        if (Array.isArray(newId) && includePositionGroups) {
          positionIds = extractPositionIdsOptions(newId, positionsGroups);
        }

        /* Hack to know that a new position will be added, in case of adding do not use input on change */
        const openAddPosition = typeof newId === 'string' || newId === null;

        if (inputOnChange && !openAddPosition) {
          return inputOnChange?.(newId);
        }
        return onChangePosition(positionIds, input, isAdded, addedData);
      },
      [includePositionGroups, inputOnChange, onChangePosition, positionsGroups]
    );

    return (
      <>
        <SelectField
          ref={ref}
          name={name}
          title={hideTitle ? '' : title ?? t('position')}
          options={options}
          onChange={onChange}
          usePortal
          isMulti={isMulti}
          creatable={enableAddNewPosition}
          formatOption={({ value, label, ...rest }) => ({
            value,
            label: <PositionSelectFieldOption value={value} label={label} />,
            ...rest
          })}
          {...restSelectProps}
          placeholder={restSelectProps?.placeholder}
        />

        {positionModal && (
          <PositionModal
            selectedPosition={{
              slug: isMulti
                ? typeof positionModal[positionModal.length - 1] === 'string'
                  ? positionModal[positionModal.length - 1]
                  : ''
                : typeof positionModal === 'string'
                ? positionModal
                : undefined
            }}
            className={modalClassName}
            onConfirm={onAdd}
            onClose={() => setPositionModal(false)}
          />
        )}
      </>
    );
  }
);

export default PositionsSelectField;
